import React, { useState } from "react";
import mockData from "../../services/mockAppAnalytics.json";
import { useNavigate, useParams } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
export const apps = [
  {
    id: "mapp1",
    name: "Steel Fitness App",
    packageName: "com.steel.fitnessapp",
    data: mockData,
    liveDate: "2025-12-10",
  },
  {
    id: "mapp2",
    name: "Yoga Pro App",
    packageName: "com.yoga.pro",
    data: mockData,
    liveDate: "2024-11-15",
  },
];

export const KpiCard: React.FC<{ title: string; value: string }> = ({
  title,
  value,
}) => (
  <div
    style={{
      flex: 1,
      background: "#FFFFFF",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
    }}
  >
    <div style={{ fontSize: "14px", color: "#888" }}>{title}</div>
    <div style={{ fontSize: "24px", fontWeight: 700, color: "#714B67" }}>
      {value}
    </div>
  </div>
);
interface MobileAppsPanelProps {
  section: string;
}
interface DailyMetric {
  date: string;
  country: string;
  appVersion: string;
  installs: number;
  firstTimeInstallers: number;
  uninstalls: number;
}

export const MobileAppsPanel: React.FC<MobileAppsPanelProps> = ({
  section,
}) => {
  const navigate = useNavigate();
  const { appSlug } = useParams<{ appSlug: string }>();

  const selectedApp = apps.find((app) => app.id === appSlug);
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedVersion, setSelectedVersion] = useState("All");
  const [dateRange, setDateRange] = useState("7"); // 7 | 30 | all

  const allRows: DailyMetric[] = selectedApp?.data.dailyMetrics || [];

  /* ================================
       ALL APPS VIEW
    ================================= */

  if (section === "allApps") {
    return (
      <div>
        <h2>Your Mobile Apps</h2>

        <div style={{ marginTop: 20, display: "flex", gap: 20 }}>
          {apps.map((app) => (
            <div
              key={app.id}
              onClick={() => navigate(`/dashboard/mobile/analytics/${app.id}`)} // ✅ FIXED
              style={{
                background: "#F4F6FA",
                padding: 20,
                borderRadius: 12,
                width: 300,
                cursor: "pointer",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#10B981",
                  background: "#E6F9F0",
                  padding: "4px 8px",
                  borderRadius: "20px",
                }}
              >
                🚀{" "}
                {app.liveDate
                  ? `Live since ${new Date(app.liveDate).toLocaleDateString()}`
                  : "Coming Soon"}
              </div>
              <h3>{app.name}</h3>
              <p style={{ fontSize: 12 }}>{app.packageName}</p>

              <button
                style={{
                  marginTop: 10,
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: "#714B67",
                  color: "white",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  localStorage.setItem("default_app", app.id); // ✅ HERE
                  //alert(`${app.name} set as default`);
                }}
              >
                Make Default
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ================================
       APP ANALYTICS VIEW
    ================================= */

  if (section !== "appAnalytics") return null;

  const uniqueCountries: string[] = Array.from(
    new Set(allRows.map((r) => r.country))
  );
  const uniqueVersions: string[] = Array.from(
    new Set(allRows.map((r) => r.appVersion))
  );

  const sortedRows = [...allRows].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const uniqueDates = Array.from(new Set(sortedRows.map((r) => r.date)));

  let allowedDates = uniqueDates;

  if (dateRange === "7") {
    allowedDates = uniqueDates.slice(-7);
  }

  if (dateRange === "30") {
    allowedDates = uniqueDates.slice(-30);
  }
  const filteredRows = sortedRows
    .filter((row) => allowedDates.includes(row.date))
    .filter((row) =>
      selectedCountry === "All" ? true : row.country === selectedCountry
    )
    .filter((row) =>
      selectedVersion === "All" ? true : row.appVersion === selectedVersion
    );

  const totalInstalls = filteredRows.reduce(
    (sum, row) => sum + row.installs,
    0
  );

  const totalUninstalls = filteredRows.reduce(
    (sum, row) => sum + row.uninstalls,
    0
  );

  const totalNewUsers = filteredRows.reduce(
    (sum, row) => sum + row.firstTimeInstallers,
    0
  );

  const netGrowth = totalInstalls - totalUninstalls;
  /* ===== Minimal Trend Aggregation ===== */

  const trendData = Object.values(
    filteredRows.reduce((acc: any, row) => {
      if (!acc[row.date]) {
        acc[row.date] = {
          date: row.date,
          installs: 0,
          uninstalls: 0,
        };
      }

      acc[row.date].installs += row.installs;
      acc[row.date].uninstalls += row.uninstalls;

      return acc;
    }, {})
  ).sort(
    (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div>
      <h2>{selectedApp?.name}</h2>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: 10, marginTop: 15 }}>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
        >
          <option value="All">All Countries</option>
          {uniqueCountries.map((country) => (
            <option key={country}>{country}</option>
          ))}
        </select>

        <select
          value={selectedVersion}
          onChange={(e) => setSelectedVersion(e.target.value)}
        >
          <option value="All">All Versions</option>
          {uniqueVersions.map((version) => (
            <option key={version}>{version}</option>
          ))}
        </select>
      </div>

      {/* KPI CARDS */}
      <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
        <KpiCard
          title="Total Installs"
          value={totalInstalls.toLocaleString()}
        />
        <KpiCard title="New Users" value={totalNewUsers.toLocaleString()} />
        <KpiCard title="Uninstalls" value={totalUninstalls.toLocaleString()} />
        <KpiCard title="Net Growth" value={netGrowth.toLocaleString()} />
      </div>
      <div
        style={{
          marginTop: 30,
          background: "#F4F6FA",
          padding: 20,
          borderRadius: 12,
        }}
      >
        <h3 style={{ marginBottom: 15 }}>Daily Install / Uninstall Trend</h3>

        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="installs"
              stroke="#714B67"
              strokeWidth={3}
            />
            <Line
              type="monotone"
              dataKey="uninstalls"
              stroke="#FF6B6B"
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
